// O dinheiro da Central do Perito (fase B): os totais dos honorários, a série por mês, o Carnê-Leão e o fluxo
// financeiro judicial. Portado de `src/lib/carne-leao.ts`, `src/lib/fluxo-judicial.ts` e das contas que as telas
// de lá faziam por dentro (`index.tsx` e `financeiro.tsx`), em 03/11. Puro: a tela passa o estado e o relógio.
//
// O que mudou em relação a lá, e por quê:
//
//  - O RECEBIDO conta no mês em que ENTROU. Lá o painel e o gráfico somavam o recebido no mês do VENCIMENTO: o
//    honorário que vencia em agosto e foi pago em outubro aparecia como "recebido em agosto", e o "recebido no
//    mês" de outubro não o contava. Aqui a data do recebido é `dataDoRecebimento`, num lugar só, e é a MESMA do
//    Carnê-Leão de lá (a data do recebimento, e, sem ela, o vencimento).
//  - Os dias e os meses são os de São Paulo (`datas.ts`): lá o mês saía de `new Date("AAAA-MM-DD").getMonth()`,
//    que no dia 1 às 21h já é o mês anterior.
//  - O Carnê-Leão não sugere o IRRF. Lá a sugestão vinha da tabela de maio/2025 escrita no código; o hub tem a
//    tabela no banco e o redutor de 2026 (Lei 15.270), e as fontes divergem sobre o redutor valer no cálculo
//    mensal. Decisão do Enzo (03/11): sem sugestão, o aluno informa o IRRF retido que está no comprovante.
import { diaDe, diasAte, hoje, mesDe } from "./datas";
import { FAIXAS_FLUXO, etapaNumDe } from "./pipeline";
import type { Honorario, Pericia } from "./tipos";

/** O que falta receber de um honorário (nunca negativo). */
export const aReceber = (h: Honorario) => Math.max(0, h.valor - h.recebido);

/** O dia em que o recebido ENTROU: a data do recebimento e, sem ela, o vencimento. Nulo se não há data. */
export function dataDoRecebimento(h: Honorario): string | null {
  return diaDe(h.dataRecebimento) ?? diaDe(h.vencimento);
}

export interface TotaisDeHonorarios {
  total: number;
  recebido: number;
  aReceber: number;
  lancamentos: number;
}

export function totaisDeHonorarios(honorarios: Honorario[]): TotaisDeHonorarios {
  return honorarios.reduce<TotaisDeHonorarios>(
    (t, h) => ({ total: t.total + h.valor, recebido: t.recebido + h.recebido, aReceber: t.aReceber + aReceber(h), lancamentos: t.lancamentos + 1 }),
    { total: 0, recebido: 0, aReceber: 0, lancamentos: 0 },
  );
}

export const todosOsHonorarios = (pericias: Pericia[]) => pericias.flatMap((p) => p.honorarios);

/** O recebido que ENTROU no mês de hoje, em São Paulo. */
export function recebidoNoMes(pericias: Pericia[], agora: Date = new Date()): number {
  const mes = hoje(agora).slice(0, 7);
  return todosOsHonorarios(pericias).reduce((a, h) => (h.recebido > 0 && mesDe(dataDoRecebimento(h)) === mes ? a + h.recebido : a), 0);
}

export interface MesDaSerie {
  /** AAAA-MM */
  mes: string;
  /** O valor dos honorários que VENCEM no mês. */
  previsto: number;
  /** O recebido que ENTROU no mês. */
  recebido: number;
}

/** Os últimos `n` meses, do mais antigo ao de hoje. */
export function serieMensal(pericias: Pericia[], n: number, agora: Date = new Date()): MesDaSerie[] {
  const [a, m] = hoje(agora).split("-").map(Number);
  const meses: MesDaSerie[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(a, m - 1 - i, 1));
    meses.push({ mes: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`, previsto: 0, recebido: 0 });
  }
  const porMes = new Map(meses.map((x) => [x.mes, x]));
  for (const h of todosOsHonorarios(pericias)) {
    const venc = porMes.get(mesDe(h.vencimento) ?? "");
    if (venc) venc.previsto += h.valor;
    const rec = h.recebido > 0 ? porMes.get(mesDe(dataDoRecebimento(h)) ?? "") : undefined;
    if (rec) rec.recebido += h.recebido;
  }
  return meses;
}

// ── O Carnê-Leão ──────────────────────────────────────────────────────────────────────────────────────────

export type VisaoDoCarne = "mensal" | "trimestral" | "anual";

export interface Lancamento {
  periciaId: string;
  honorarioId: string;
  /** AAAA-MM-DD */
  data: string;
  descricao: string;
  numeroProcesso: string;
  fontePagadora: string;
  cpfCnpj: string;
  bruto: number;
  inss: number;
  irrf: number;
  liquido: number;
}

export interface ResumoDoCarne {
  bruto: number;
  inss: number;
  irrf: number;
  liquido: number;
}

export interface LinhaDoCarne extends ResumoDoCarne {
  chave: string;
  rotulo: string;
  lancamentos: Lancamento[];
}

/** Os honorários com valor recebido no ano, pela data em que entraram, do mais antigo ao mais novo. */
export function listarLancamentos(pericias: Pericia[], ano: number): Lancamento[] {
  const out: Lancamento[] = [];
  for (const p of pericias) {
    for (const h of p.honorarios) {
      if (h.recebido <= 0) continue;
      const data = dataDoRecebimento(h);
      if (!data || Number(data.slice(0, 4)) !== ano) continue;
      const inss = h.inss ?? 0;
      const irrf = h.irrf ?? 0;
      out.push({
        periciaId: p.id, honorarioId: h.id, data, descricao: h.descricao, numeroProcesso: p.numeroProcesso,
        fontePagadora: p.fontePagadora ?? "", cpfCnpj: p.cpfCnpjFonte ?? "",
        bruto: h.recebido, inss, irrf, liquido: h.recebido - inss - irrf,
      });
    }
  }
  return out.sort((a, b) => a.data.localeCompare(b.data) || a.honorarioId.localeCompare(b.honorarioId));
}

/** Os anos com algum recebido, do mais novo ao mais velho, e sempre o ano de hoje. */
export function anosDisponiveis(pericias: Pericia[], agora: Date = new Date()): number[] {
  const anos = new Set<number>([Number(hoje(agora).slice(0, 4))]);
  for (const h of todosOsHonorarios(pericias)) {
    const data = h.recebido > 0 ? dataDoRecebimento(h) : null;
    if (data) anos.add(Number(data.slice(0, 4)));
  }
  return [...anos].sort((a, b) => b - a);
}

export function resumir(lancamentos: Lancamento[]): ResumoDoCarne {
  return lancamentos.reduce<ResumoDoCarne>(
    (r, l) => ({ bruto: r.bruto + l.bruto, inss: r.inss + l.inss, irrf: r.irrf + l.irrf, liquido: r.liquido + l.liquido }),
    { bruto: 0, inss: 0, irrf: 0, liquido: 0 },
  );
}

const MESES_CURTOS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const mesDoLancamento = (l: Lancamento) => Number(l.data.slice(5, 7)) - 1;

/** As linhas do Carnê-Leão: só os períodos que têm lançamento (como lá). */
export function agruparCarne(lancamentos: Lancamento[], visao: VisaoDoCarne, ano: number): LinhaDoCarne[] {
  if (visao === "anual") return [{ chave: String(ano), rotulo: String(ano), ...resumir(lancamentos), lancamentos }];
  if (visao === "trimestral") {
    return [0, 1, 2, 3]
      .map((t) => {
        const itens = lancamentos.filter((l) => Math.floor(mesDoLancamento(l) / 3) === t);
        return { chave: `T${t + 1}`, rotulo: `${t + 1}º trimestre`, ...resumir(itens), lancamentos: itens };
      })
      .filter((l) => l.lancamentos.length > 0);
  }
  return MESES_CURTOS.map((m, i) => {
    const itens = lancamentos.filter((l) => mesDoLancamento(l) === i);
    return { chave: `M${i + 1}`, rotulo: `${m}/${String(ano).slice(2)}`, ...resumir(itens), lancamentos: itens };
  }).filter((l) => l.lancamentos.length > 0);
}

// ── O fluxo financeiro judicial ──────────────────────────────────────────────────────────────────────────

export interface FaixaCalculada {
  id: string;
  rotulo: string;
  cor: string;
  valor: number;
  quantas: number;
  pct: number;
}

/** O valor dos honorários de cada perícia, somado pela faixa da ETAPA em que ela está. */
export function calcularFaixas(pericias: Pericia[]): { faixas: FaixaCalculada[]; total: number } {
  const faixas = FAIXAS_FLUXO.map((f) => {
    const itens = pericias.filter((p) => (f.etapas as readonly number[]).includes(etapaNumDe(p)));
    return { id: f.id, rotulo: f.label, cor: f.cor, valor: itens.reduce((a, p) => a + totaisDeHonorarios(p.honorarios).total, 0), quantas: itens.length, pct: 0 };
  });
  const total = faixas.reduce((a, f) => a + f.valor, 0);
  for (const f of faixas) f.pct = total ? (f.valor / total) * 100 : 0;
  return { faixas, total };
}

/** O que falta receber com vencimento de hoje até 7, 30, 60 e 90 dias (as janelas se acumulam, como lá). */
export function proximosRecebimentos(pericias: Pericia[], agora: Date = new Date()): Array<{ dias: number; valor: number; quantos: number }> {
  const janelas = [7, 30, 60, 90].map((dias) => ({ dias, valor: 0, quantos: 0 }));
  for (const h of todosOsHonorarios(pericias)) {
    const aberto = aReceber(h);
    const dias = diasAte(h.vencimento, agora);
    if (aberto <= 0 || dias === null || dias < 0) continue;
    for (const j of janelas) {
      if (dias <= j.dias) {
        j.valor += aberto;
        j.quantos += 1;
      }
    }
  }
  return janelas;
}

/** As perícias na etapa 9 (a liberação), com o que falta levantar, do maior valor ao menor. */
export function valoresLiberados(pericias: Pericia[]): Array<{ pericia: Pericia; total: number; recebido: number; aberto: number }> {
  return pericias
    .filter((p) => etapaNumDe(p) === 9)
    .map((p) => {
      const t = totaisDeHonorarios(p.honorarios);
      return { pericia: p, total: t.total, recebido: t.recebido, aberto: t.aReceber };
    })
    .sort((a, b) => b.aberto - a.aberto || a.pericia.id.localeCompare(b.pericia.id));
}
